<template>
    <v-container>
        <v-row>
            <v-col cols="12">
                <v-data-table
                    :headers="headers"
                    :items="clubMedalCounts"
                    :items-per-page="50"
                    :multi-sort="true"
                    :footer-props="{ 'items-per-page-options': [15, 30, 50, 100, -1] }"
                    class="elevation-1"
                    no-data-text="No results yet."
                    no-results-text="No results matching your search criteria."
                >
                    <template #[`header.total_points`]="{ header }">
                        <div class="d-flex">
                            <div>{{ header.text }}</div>
                            <v-tooltip bottom>
                                <template #activator="{ on }">
                                    <v-icon x-small v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <p>
                                    <strong>Definition</strong>: <br />
                                    The total score of the club based on the medals won, with points assigned as 3 for
                                    gold, 2 for silver, and 1 for bronze.
                                </p>
                                <p>
                                    <strong>Purpose</strong>: <br />
                                    Provides a weighted total that reflects the quality of medals won by the club.
                                </p>
                            </v-tooltip>
                        </div>
                    </template>

                    <template #[`header.medals_per_athlete`]="{ header }">
                        <div class="d-flex">
                            <div>{{ header.text }}</div>
                            <v-tooltip bottom>
                                <template #activator="{ on }">
                                    <v-icon x-small v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <p>
                                    <strong>Definition</strong>: <br />
                                    The average number of medals won per athlete in the club.
                                </p>
                                <p>
                                    <strong>Purpose</strong>: <br />
                                    Measures the medal-winning efficiency relative to the number of athletes.
                                </p>
                            </v-tooltip>
                        </div>
                    </template>

                    <template #[`header.points_per_athlete`]="{ header }">
                        <div class="d-flex">
                            <div>{{ header.text }}</div>
                            <v-tooltip bottom>
                                <template #activator="{ on }">
                                    <v-icon x-small v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <p>
                                    <strong>Definition</strong>: <br />
                                    The average number of points scored per athlete in the club.
                                </p>
                                <p>
                                    <strong>Purpose</strong>: <br />
                                    Measures the scoring efficiency, considering both the number and type of medals won,
                                    relative to the number of athletes.
                                </p>
                            </v-tooltip>
                        </div>
                    </template>

                    <template #[`header.medal_efficiency`]="{ header }">
                        <div class="d-flex">
                            <div>{{ header.text }}</div>
                            <v-tooltip bottom>
                                <template #activator="{ on }">
                                    <v-icon x-small v-on="on">mdi-information-outline</v-icon>
                                </template>
                                <p>
                                    <strong>Definition</strong>: <br />
                                    The ratio of medals won to the number of athletes as a percentage.
                                </p>
                                <p>
                                    <strong>Purpose</strong>: <br />
                                    Indicates the club's effectiveness in winning medals, adjusted for the number of
                                    athletes.
                                </p>
                            </v-tooltip>
                        </div>
                    </template>

                    <template #[`item.medal_count`]="{ item }">
                        <div class="d-flex">
                            <div>
                                <v-icon>mdi-medal</v-icon>
                            </div>
                            <div style="margin-top: 0.15rem">
                                <strong class="text--white">{{ item.medal_count }}</strong>
                            </div>
                        </div>
                    </template>

                    <template #[`item.club.name`]="{ item }">
                        <div class="d-flex">
                            <div>
                                <scorsync-avatar :person="clubModel(item.club)" type="club" />
                            </div>
                            <div class="my-auto ml-2">
                                {{ item.club.name }}
                            </div>
                        </div>
                    </template>

                    <template #[`item.total_points`]="{ item }">
                        <v-chip small>
                            <strong>{{ item.total_points }}</strong>
                        </v-chip>
                    </template>

                    <template #[`item.gold_count`]="{ item }">
                        <v-avatar color="yellow darken-2" size="24">
                            <strong class="text--white">{{ item.gold_count }}</strong>
                        </v-avatar>
                    </template>

                    <template #[`item.silver_count`]="{ item }">
                        <v-avatar color="grey" size="24">
                            <strong class="text--white">{{ item.silver_count }}</strong>
                        </v-avatar>
                    </template>

                    <template #[`item.bronze_count`]="{ item }">
                        <v-avatar color="brown" size="24">
                            <strong class="text--white">{{ item.bronze_count }}</strong>
                        </v-avatar>
                    </template>

                    <template #[`item.medals_per_athlete`]="{ item }">
                        {{ numeral(item.medals_per_athlete).format('0.00') }}
                    </template>

                    <template #[`item.points_per_athlete`]="{ item }">
                        {{ numeral(item.points_per_athlete).format('0.00') }}
                    </template>

                    <template #[`item.medal_efficiency`]="{ item }">
                        {{ numeral(item.medal_efficiency).format('0%') }}
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import numeral from 'numeral';
import ScorsyncAvatar from '@/Components/ScorsyncAvatar.vue';
import Club from '@/Models/Club.js';

export default {
    name: 'MedalCount',
    components: { ScorsyncAvatar },

    props: {
        clubMedalCounts: {
            type: Array,
            required: true,
        },
    },

    data: () => ({
        headers: [
            { text: 'Club Name', value: 'club.name' },
            { text: 'Athletes', value: 'athlete_count' },
            { text: 'Total Points', value: 'total_points' },
            { text: 'Total Medals', value: 'medal_count' },
            { text: 'Gold', value: 'gold_count' },
            { text: 'Silver', value: 'silver_count' },
            { text: 'Bronze', value: 'bronze_count' },
            { text: 'Podium', value: 'podium_count' },
            { text: 'Medals per Athlete', value: 'medals_per_athlete' },
            { text: 'Points per Athlete', value: 'points_per_athlete' },
            { text: 'Medal Efficiency', value: 'medal_efficiency' },
        ],
    }),

    created() {
        Club.insert({ data: this.clubMedalCounts.map(clubMedalCount => clubMedalCount.club) });
    },

    methods: {
        numeral,

        clubModel(club) {
            return Club.find(club.id);
        },
    },
};
</script>

<style scoped></style>
